import ColorPicker from '@src/components/common/ColorPicker';
import { PropertyNumericalInput } from '@src/components/sidepanel/PropertyNumericalInput';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import HighlightIcon from '@src/svgs/HighlightIcon';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';

const Colors: Record<string, string> = {
  white: 'rgba(255, 255, 255, 1)',
  black: 'rgba(0, 0, 0, 1)',
  transparent: 'rgba(0, 0, 0, 0)',
  red: 'rgba(255, 0, 0, 1)',
  green: 'rgba(0, 255, 0, 1)',
  orange: 'rgba(255, 165, 0, 1)',
  blue: 'rgba(0, 0, 255, 1)',
  yellow: 'rgba(255, 255, 0, 1)',
};

type Props = {
  action: (propertyMap: Partial<KaraokeConfig>) => void;
  defaultValue?: string | null;
  opacity: number;
};

const formatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

// Encapsulated color picker + opacity numeric input for karaoke backgrounds.
const BackgroundColorOpacityControl = (props: Props) => {
  const { action, defaultValue, opacity } = props;
  const videoCreator = useVideoCreatorStore();
  const [openPicker, togglePicker] = useState<DOMRect | null>(null);

  // ColorPicker portals itself and owns dismissal — no wrapper-side outside-click hook needed.

  const pickerClose = videoCreator.textBrandingService.closeActiveState;
  useEffect(() => {
    if (pickerClose) {
      togglePicker(null);
      videoCreator.textBrandingService.closeActiveState = false;
    }
  }, [pickerClose, videoCreator.textBrandingService]);

  let currColor = defaultValue ?? 'rgba(0, 0, 0, 1)';
  if (currColor in Colors) {
    currColor = Colors[currColor];
  }
  const rgbaArray = currColor?.match(/\d+(\.\d+)?/g)?.map(Number) || [];

  return (
    <Main>
      {openPicker && (
        <ColorPicker
          defaultValue={defaultValue}
          getValue={colorValue => {
            let color = colorValue;
            if (!colorValue) color = null;
            action({ background_color: color });
          }}
          pos={openPicker}
          onClose={() => {
            togglePicker(null);
          }}
        />
      )}
      <Content
        isSelected={!!defaultValue}
        onClick={e => {
          if (openPicker) return togglePicker(null);
          if (!defaultValue) {
            action({ background_color: 'rgba(255, 255, 255, 0.7)' });
          }
          return togglePicker(e.currentTarget.getBoundingClientRect());
        }}
      >
        <Highlight>
          <HighlightIcon />
          {defaultValue && <HighlightStroke selectedItem={defaultValue} />}
        </Highlight>
      </Content>
      <HighlightDeg onClick={() => togglePicker(null)}>
        <PropertyNumericalInput
          defaultValue={formatter.format((opacity ?? 1) * 100)}
          unit="%"
          propertyName="opacity"
          manualKaraoke
          padding="12px 8px"
          getInputValue={(_key: string, val: string | number) => {
            const newOpacity = parseFloat(String(val || 0)) / 100;

            if (
              !Number.isNaN(newOpacity) &&
              newOpacity >= 0 &&
              newOpacity <= 1
            ) {
              // Ensure we have at least RGB values before constructing color
              const colorArray =
                rgbaArray.length >= 3
                  ? [...rgbaArray]
                  : [0, 0, 0, ...rgbaArray];

              if (colorArray.length === 3) {
                colorArray.push(newOpacity);
              } else if (colorArray.length >= 4) {
                colorArray[3] = newOpacity;
              }

              const newColor = `rgba(${colorArray.slice(0, 4).join(', ')})`;
              action({ background_color: newColor });
            }
          }}
        />
      </HighlightDeg>
    </Main>
  );
};

export default BackgroundColorOpacityControl;

const Main = styled.div`
  display: flex;
  gap: 8px;
  position: relative;
  width: fit-content;
`;

const Content = styled.div<{ isSelected: boolean }>`
  position: relative;
  width: 40px;
  height: 40px;
  aspect-ratio: 1/1;
  border: 1px solid
    ${props => (props.isSelected ? 'var(--accent)' : 'var(--border)')};
  background-color: ${props =>
    props.isSelected ? 'var(--accent)' : 'transparent'};
  border-radius: var(--radius);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  svg {
    path {
      stroke: ${props =>
        props.isSelected ? 'var(--background)' : 'var(--text-muted)'};
    }
  }
`;

const HighlightStroke = styled.div<{ selectedItem: string | null }>`
  background-color: ${props => props.selectedItem};
  width: 18px;
  height: 5px;
`;

const Highlight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  width: 100%;
`;

const HighlightDeg = styled.div`
  padding: 0px;
  max-width: 60px;
  height: 42px;
  div {
    height: 100%;
    input {
      height: 42px !important;
      box-sizing: border-box;
    }
  }
`;
